import { Component, OnInit } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { QueryType } from 'app/enums/query-type.enum';
import { ElasticSearchResult } from 'app/interfaces/elastic-search-result.model';
import { SearchService } from 'app/modules/search-module/services/search.service';
import { switchMap, tap, map } from 'rxjs/operators';

@Component({
  selector: 'app-project-search',
  templateUrl: './project-search.component.html',
  styleUrls: ['./project-search.component.scss'],
})
export class ProjectSearchComponent implements OnInit {
  $results: Observable<ElasticSearchResult>;
  $loadingResults: BehaviorSubject<boolean> = new BehaviorSubject(true);
  $currentPage: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  projects = [];
  aggregations = [];

  allAggregations = [];
  queryTypeLookup = {
    nested: QueryType.NESTED,
    range: QueryType.RANGE,
    checkbox: QueryType.CHECKBOX,
    dropdown: QueryType.DROPDOWN,
    matchall: QueryType.MATCHALL,
  };

  currentPage = 0;
  size = 24;
  settings: any = { hits: { total: 0 } };
  filterBody: any = { filters: {} };
  q: string;
  t: any;

  queries = [];

  constructor(private searchService: SearchService, private router: Router, private route: ActivatedRoute) {}

  async ngOnInit() {
    this.allAggregations = this.fillAndReturnAggregations(await this.searchService.getAggregations().toPromise());
    // Activate initial filters in UI
    for (const prop in this.route.snapshot.queryParams) {
      if (prop !== 'page' && prop !== 'q') {
        const agg = this.allAggregations.find(a => a.Meta.title === prop);
        const query = {
          Type: this.queryTypeLookup[agg.Meta.type],
          Field: agg.Meta.title,
          Values: this.route.snapshot.queryParams[prop].split(','),
        };

        this.queries = [...this.queries, query];
        this.filterBody.filters[prop] = query;
      }
    }

    // Set initial page
    if (this.route.snapshot.queryParams.page) {
      this.currentPage = +this.route.snapshot.queryParams.page;
    }

    // Add search term filter
    if (this.route.snapshot.queryParams.q) {
      this.q = this.route.snapshot.queryParams.q;
      this.queries.push({
        Type: QueryType.QUERY,
        Field: 'q',
        Values: [this.q],
      });
    }

    // this.loadProjects(this.size, this.size * this.currentPage);

    this.route.queryParams
      .pipe(
        tap((params: Params) => {
          const page = +(params['page'] || 0);
          if (page !== this.$currentPage.value) {
            this.$currentPage.next(page);
          }
        })
      )
      .subscribe(a => {});

    this.$currentPage.subscribe(a => {
      this.loadProjects(this.size, this.size * a);
    });
  }

  keyUp($event: KeyboardEvent) {
    if ($event.keyCode === 13) {
      return;
    }

    if (this.t) {
      clearTimeout(this.t);
    }

    this.t = setTimeout(() => {
      this.filter();
    }, 300);
  }

  loadProjects(size, from) {
    this.$results = this.searchService.find(size, from, this.queries);

    this.$results.subscribe(a => {
      this.projects = a.hits;
      this.settings.hits.total = a.hitcount;

      // if (this.aggregations.length <= 0) {
      this.fillAggregations(a.aggregations);

      this.aggregations.sort((a: any, b: any) => {
        if (a.Meta.order < b.Meta.order) {
          return -1;
        } else if (a.Meta.order > b.Meta.order) {
          return 1;
        } else {
          return 0;
        }
      });

      this.$loadingResults.next(false);
    });
  }

  fillAggregationsRecursive(aggs: any[], dest: any[]): any[] {
    // tslint:disable-next-line:forin
    for (const x in aggs) {
      const a = aggs[x];
      if (a.Meta && (Object.getOwnPropertyNames(a).indexOf('Items') >= 0 || Object.getOwnPropertyNames(a).indexOf('Value') >= 0)) {
        dest = [...dest, a];
      } else {
        dest = this.fillAggregationsRecursive(a.Aggregations, dest);
      }
    }
    return dest;
  }

  fillAggregations(aggs: any[]) {
    const newAggregations = this.fillAggregationsRecursive(aggs, []);
    this.aggregations = Object.assign([], this.aggregations, newAggregations);

    if (this.filterBody) {
      // tslint:disable-next-line:forin
      for (const a in this.filterBody.filters) {
        const filter = this.filterBody.filters[a];
        const aggMatch = this.aggregations[this.aggregations.map(b => b.Meta.title).indexOf(filter.Field)];
        aggMatch.selectedValues = filter.Values;
        if (aggMatch.Items) {
          aggMatch.Items.forEach(i => (i.checked = filter.Values.indexOf(i.Key) >= 0));
        }
      }
    }
  }

  fillAndReturnAggregations(aggs: any[]) {
    const newAggregations = this.fillAggregationsRecursive(aggs, []);
    return Object.assign([], this.aggregations, newAggregations);
  }

  filter() {
    const params = {};
    this.currentPage = 0;

    params['page'] = this.currentPage;

    this.$loadingResults.next(true);

    if (this.filterBody && this.filterBody.filters) {
      this.queries = [];
      // tslint:disable-next-line:forin
      for (const prop in this.filterBody.filters) {
        this.queries.push(this.filterBody.filters[prop]);

        this.filterBody.filters[prop].Values.forEach(val => {
          if (params[prop]) {
            params[prop] = params[prop] + ',' + val;
          } else {
            params[prop] = val;
          }
        });
      }
    }

    if (this.q && this.q !== '') {
      params['q'] = this.q;

      this.queries.push({
        Type: QueryType.QUERY,
        Field: 'q',
        Values: [this.q],
      });
    } else {
      params['q'] = null;
    }

    this.router.navigate(['projects'], { queryParams: params });

    this.loadProjects(this.size, 0);
  }
}
